import React from 'react';
import { AnswerFeedback, GameStatus, Question } from '../types';
import { shuffleArray } from '../utils/shuffleUtils';
import { GameControls } from './GameControls';
import { AnswerButtons, AnswerOptionState } from './AnswerButtons';
import { HelpButtons } from './HelpButtons';
import { PlayerArea } from './PlayerArea';
import { playSound } from '../utils/soundEffects';
import './GameArea.css';

interface GameAreaProps {
  status: GameStatus;
  gameActualQuestion: Question;
  actualQuestion: Question;
  answerFeedback: AnswerFeedback;
  title: string;
  onSelectOption: (value: string) => void;
  onStart: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onResolve: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onNext: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onUseHelpItem: (itemIndex: number) => void;
  showCountdown: boolean;
  setShowCountdown: (value: boolean) => void;
  startDisabled?: boolean;
}

const GameAreaComponent: React.FC<GameAreaProps> = ({
  status,
  gameActualQuestion,
  actualQuestion,
  answerFeedback,
  title,
  onSelectOption,
  onStart,
  onResolve,
  onNext,
  onReset,
  onUseHelpItem,
  showCountdown,
  setShowCountdown,
  startDisabled,
}) => {
  // Memoized so HelpButtons (wrapped in React.memo) doesn't see a new array
  // identity - and thus re-render - unless one of the 4 lifeline flags changed.
  const helpItems = React.useMemo(
    () => [status.item1, status.item2, status.item3, status.item4],
    [status.item1, status.item2, status.item3, status.item4]
  );

  const questionText = gameActualQuestion.pregunta !== ""
    ? gameActualQuestion.pregunta
    : actualQuestion.pregunta;

  // Memoize and shuffle answers only when the question changes
  const answers = React.useMemo(() => {
    const rawAnswers: [string, string, string, string] = [
      gameActualQuestion["1"] !== "" ? gameActualQuestion["1"] : actualQuestion["1"],
      gameActualQuestion["2"] !== "" ? gameActualQuestion["2"] : actualQuestion["2"],
      gameActualQuestion["3"] !== "" ? gameActualQuestion["3"] : actualQuestion["3"],
      gameActualQuestion["4"] !== "" ? gameActualQuestion["4"] : actualQuestion["4"],
    ];
    return shuffleArray(rawAnswers) as [string, string, string, string];
  }, [gameActualQuestion, actualQuestion]); // Re-shuffle only on question change

  // Derive each button's visual state from the shuffled answer values (not indices),
  // matching how correctness itself is compared by value. Memoized for the same
  // reason as helpItems above - AnswerButtons is wrapped in React.memo.
  const optionStates = React.useMemo(() => answers.map((value): AnswerOptionState => ({
    selected: answerFeedback.selectedValue === value,
    correct: answerFeedback.resolved && value === answerFeedback.correctValue,
    wrong: answerFeedback.resolved && !answerFeedback.isCorrect && value === answerFeedback.selectedValue,
    disabled: answerFeedback.disabledValues.includes(value),
  })) as [AnswerOptionState, AnswerOptionState, AnswerOptionState, AnswerOptionState], [answers, answerFeedback]);

  const handleSelectOption = React.useCallback((value: string) => {
    playSound('button');
    onSelectOption(value);
  }, [onSelectOption]);

  return (
    <div className="game-area">
      <div className="game-content-wrapper">
        <div className="camera-row w-100 d-flex justify-content-between gap-4 mb-2">
          <PlayerArea
            className="flex-grow-1"
            placeholder="STREAMER"
            label="Cámara del Streamer"
          />
          <PlayerArea
            className="flex-grow-1"
            placeholder="JUGADOR"
            label="Cámara del Jugador"
          />
        </div>

        <div className="w-100 mb-2">
          <HelpButtons items={helpItems} onUseHelpItem={onUseHelpItem} />
        </div>

        <div id="question-container" className="w-100 d-flex justify-content-center mb-3">
          <div id="question" key={status.phase} className="question-transition">
            {status.phase >= 0 ? questionText : "-"}
          </div>
        </div>

        <div className="w-100 answers-transition" key={status.phase}>
          <AnswerButtons
            answers={answers}
            optionStates={optionStates}
            onSelectOption={handleSelectOption}
          />
        </div>

        <div className="w-100 mt-2">
          <GameControls
            status={status}
            onStart={onStart}
            onResolve={onResolve}
            onNext={onNext}
            onReset={onReset}
            showCountdown={showCountdown}
            setShowCountdown={setShowCountdown}
            startDisabled={startDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export const GameArea = React.memo(GameAreaComponent);
